// 261010 UI1a — danh sách clip đã chọn để tải cho CapCut (THUẦN, không antd; video-ui 29/10: khối trên màn bước 8 đã bỏ, còn dùng cho nút chính «Tải clip cho CapCut»). Mỗi video MỘT clip ĐANG CHỌN; không lấy clip DONE chưa chọn; không gọi export / ghép.
import type { FilmEvent } from '../../types/film';
import type { WorkflowEventStatus } from '../../types/filmWorkflow';
import type { VideoExportResponse } from '../../services/api/film';

export interface CapCutRow {
  eventId: string; order: number; sceneOrder: number; label: string; state: 'OK' | 'MISSING';
  clipId?: string; url?: string; durationS?: number; fileName?: string; thumb?: string;
  /** UI2 — từ GET export: lý do thiếu, điểm cắt, thời lượng SAU cắt, nguồn clip (UPLOAD = người tải lên). */
  reason?: string; reasonCode?: string; cutInS?: number | null; cutOutS?: number | null; shownS?: number; provenance?: string | null;
}
export interface CapCutView { rows: CapCutRow[]; okCount: number; total: number; missingCount: number; totalDurationS: number }

const httpUrl = (u: unknown): string | null => (typeof u === 'string' && /^https?:\/\//i.test(u.trim()) ? u.trim() : null);

/** `video-NN.<đuôi>`; đuôi lấy từ đường dẫn URL (bỏ query), 2–4 ký tự chữ-số, mặc định mp4. */
export function downloadFileNameOf(order: number, url: string): string {
  const path = String(url ?? '').split(/[?#]/)[0];
  const m = /\.([a-z0-9]{2,4})$/i.exec(path);
  return `video-${String(order).padStart(2, '0')}.${m ? m[1].toLowerCase() : 'mp4'}`;
}

type DetailLike = Pick<FilmEvent, 'selectedClipId' | 'clips'>;

export function capCutClipsOf(events: WorkflowEventStatus[], detail?: Map<string, DetailLike> | null): CapCutView {
  const sorted = [...(events ?? [])].sort((a, b) => a.sceneOrder - b.sceneOrder || a.eventOrder - b.eventOrder);
  const rows: CapCutRow[] = sorted.map((e) => {
    const base = { eventId: e.eventId, order: e.eventOrder, sceneOrder: e.sceneOrder, label: `Video ${e.eventOrder}` };
    const thumb = httpUrl(e.card?.thumb?.url) ?? undefined;
    let clipId: string | undefined; let url: string | null = null; let durationS: number | undefined;
    const sc = e.card?.selectedClip;
    if (sc) { clipId = sc.clipId; url = httpUrl(sc.url); durationS = sc.durationS; }
    if (!url) {
      const d = detail?.get(e.eventId);
      const c = d?.selectedClipId ? (d.clips ?? []).find((x) => x.id === d.selectedClipId) : undefined;
      if (c && c.status === 'DONE' && httpUrl(c.videoUrl)) { clipId = c.id; url = httpUrl(c.videoUrl); durationS = c.durationS ?? undefined; }
    }
    if (!url) return { ...base, state: 'MISSING' as const, ...(thumb ? { thumb } : {}) };
    return { ...base, state: 'OK' as const, clipId, url, ...(durationS != null ? { durationS } : {}), fileName: downloadFileNameOf(e.eventOrder, url), ...(thumb ? { thumb } : {}) };
  });
  const ok = rows.filter((r) => r.state === 'OK');
  return {
    rows, okCount: ok.length, total: rows.length, missingCount: rows.length - ok.length,
    totalDurationS: ok.reduce((n, r) => n + (r.durationS && r.durationS > 0 ? r.durationS : 0), 0),
  };
}

/** Nguyên nhân `missing[].reason` mà BE thật phát: blocker của `videoReadinessOf` / `genPromptGate` / resolver + trạng thái clip đã chọn. */
const REASON_TEXT: Record<string, string> = {
  NO_SELECTED_CLIP: 'Chưa chọn clip', SELECTED_CLIP_NOT_FOUND: 'Clip đã chọn không còn', CLIP_PENDING: 'Clip đang tạo', CLIP_FAILED: 'Clip lỗi', CLIP_NO_URL: 'Clip chưa có file',
  NO_PROMPT: 'Chưa có prompt — dựng prompt trước',
  PROMPT_MODE_MISMATCH: 'Prompt lệch kiểu với video — đổi kiểu hoặc dựng lại prompt',
  NO_DURATION: 'Video chưa có thời lượng',
  AGENT_EVENT: 'Video do AI ngoài làm — không tạo clip ở đây',
  GENERATION_CONFIG_MISSING: 'Chưa chọn engine / độ phân giải cho video',
  GENERATION_CONFIG_INVALID: 'Cấu hình engine của video hỏng — chọn lại',
  NEEDS_SPLIT: 'Video dài quá trần của engine — máy tự chia khi phân đoạn (chặng 1); còn thấy nghĩa là chia không được, chạy lại «Phân đoạn» hoặc chia tay',
  CLIP_UNCERTAIN: 'Chưa rõ clip đã tạo chưa — cần đối soát, máy không tự tạo lại',
  CLIP_PAID_UNSAVED: 'Clip đã tạo ở nhà cung cấp nhưng chưa lưu được — cần đối soát, máy không tự tạo lại',
  CLIP_NOT_SELECTED: 'Đã có clip mới nhưng chưa chọn — chọn một bản',
  BLOCKED_UPSTREAM: 'Chờ bước trước', BLOCKED_INPUT: 'Thiếu đầu vào',
};
/** Mã kỹ thuật lạ (MÃ_VIẾT_HOA) KHÔNG hiện cho người dùng — câu chung; chữ thường thì giữ nguyên. Mã gốc vẫn ở `CapCutRow.reasonCode`. */
export const missingReasonText = (code: string): string => {
  if (REASON_TEXT[code]) return REASON_TEXT[code];
  if (/^CLIP_[A-Z_]+$/.test(code)) return 'Clip chưa dùng được';
  if (/^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$/.test(code)) return 'Chưa xuất được clip này';
  return code;
};

/** Thời lượng SAU cắt; điểm cắt không hợp lệ (ra ≤ vào, vượt độ dài, âm) ⇒ coi như không cắt. */
const shownOf = (d: number | null | undefined, a: number | null | undefined, b: number | null | undefined): number | undefined => {
  if (typeof d !== 'number' || !(d > 0)) return undefined;
  if (typeof a === 'number' && typeof b === 'number' && a >= 0 && b > a && b <= d + 1e-9) return Math.round((b - a) * 10) / 10;
  return d;
};

/** Khối CapCut từ `GET workflow/steps/VIDEO/export` (BE L3–L6): clip đã chọn + `missing[]`. Export `null` ⇒ mọi hàng thiếu (caller dùng `capCutClipsOf`). */
export function capCutFromExport(exp: VideoExportResponse | null | undefined, events: WorkflowEventStatus[]): CapCutView {
  const sorted = [...(events ?? [])].sort((a, b) => a.sceneOrder - b.sceneOrder || a.eventOrder - b.eventOrder);
  const clips = new Map((exp?.clips ?? []).map((c) => [c.eventId, c]));
  const missing = new Map((exp?.missing ?? []).map((m) => [m.eventId, m]));
  const rows: CapCutRow[] = sorted.map((e) => {
    const base = { eventId: e.eventId, order: e.eventOrder, sceneOrder: e.sceneOrder, label: `Video ${e.eventOrder}` };
    const thumb = httpUrl(e.card?.thumb?.url) ?? undefined;
    const c = clips.get(e.eventId);
    const url = c ? httpUrl(c.videoUrl) : null;
    if (c && url) {
      const dur = typeof c.durationS === 'number' ? c.durationS : undefined;
      const shownS = shownOf(c.durationS, c.cutInS, c.cutOutS);
      return {
        ...base, state: 'OK' as const, clipId: c.clipId, url, ...(dur !== undefined ? { durationS: dur } : {}), fileName: downloadFileNameOf(e.eventOrder, url),
        cutInS: c.cutInS ?? null, cutOutS: c.cutOutS ?? null, ...(shownS !== undefined ? { shownS } : {}), provenance: c.provenance ?? null, ...(thumb ? { thumb } : {}),
      };
    }
    const m = missing.get(e.eventId);
    return { ...base, state: 'MISSING' as const, reason: m ? missingReasonText(m.reason) : c ? 'Clip chưa có file tải được' : 'Chưa có trong danh sách xuất', ...(m ? { reasonCode: m.reason } : {}), ...(thumb ? { thumb } : {}) };
  });
  const ok = rows.filter((r) => r.state === 'OK');
  return { rows, okCount: ok.length, total: rows.length, missingCount: rows.length - ok.length, totalDurationS: ok.reduce((n, r) => n + (r.shownS ?? r.durationS ?? 0), 0) };
}

type DetailMap = Map<string, DetailLike> | null | undefined;
/** MỘT nguồn cho danh sách CapCut VÀ cửa tải chính: có export hợp lệ ⇒ theo export (BE là nguồn); không ⇒ rơi về workflow-status + chi tiết plan. */
export function capCutViewOf(exp: VideoExportResponse | null | undefined, events: WorkflowEventStatus[], detail?: DetailMap): CapCutView {
  return exp && Array.isArray(exp.clips) ? capCutFromExport(exp, events) : capCutClipsOf(events, detail);
}
/**
 * Cửa tải của nút chính: gọi export TRƯỚC khi dựng danh sách tải; chỉ rơi về status/detail khi export không được hỗ trợ (`enabled=false`) hoặc thất bại.
 * `fetchExport` được tiêm vào để test không cần mạng.
 */
export async function loadCapCutRows(a: { enabled: boolean; fetchExport: () => Promise<VideoExportResponse>; events: WorkflowEventStatus[]; detail?: DetailMap }): Promise<CapCutView> {
  let exp: VideoExportResponse | null = null;
  if (a.enabled) { try { exp = await a.fetchExport(); } catch { exp = null; } }
  return capCutViewOf(exp, a.events, a.detail);
}
